import { Button, Card, CardContent, CircularProgress, FormControl, Grid, MenuItem, Select, Typography } from '@mui/material';
import moment from 'moment';
import { useEffect, useMemo, useState } from 'react';
import UpdateIcon from '@mui/icons-material/Update';
import { FaTrashAlt } from 'react-icons/fa';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../../components/Breadcrumbs';
import ConfirmDialog from '../../components/Notification/ConfirmDialog';
import Notification from '../../components/Notification/Notification';
import Table from '../../components/Table';
import UserPermission from '../../components/UserPermission';
import api from '../../services/api';
import ServiceOrderRegister from '../ServiceOrderRegister/ServiceOrderRegister';
import '../Details/styles.css';

const classes = {
    root: {
        flexGrow: 1,
        margin: 20,
        marginTop: 30,
        borderRadius: '17px',
        backgroundColor: '#F5F5F5',
        boxShadow: 'none',
    },
    textTitle: {
        margin: 8,
        color: '#103168',
        fontWeight: 600,
        marginBottom: 30,
    },
    message: {
        padding: 10,
        backgroundColor: '#FFFBE8',
        color: 'rgba(0,0,0,0.8)',
        borderRadius: 7,
        width: '100%'
    },
    subTitle: {
        color: '#103168',
        fontWeight: 600,
        marginTop: 0,
        marginBottom: 12,
    },
    section: {
        border: '1px solid #d5d5d5',
        borderRadius: 10,
        padding: 16,
        marginTop: 16,
        backgroundColor: '#fff',
    },
    buttonUpdate: {
        backgroundColor: '#1551B3',
        color: '#fff',
        borderRadius: 20,
        height: '45px',
        textTransform: 'capitalize',
        fontWeight: 'bold',
        fontSize: 15,
        marginTop: '17px',
        minWidth: '220px'
    },
    labelInput: {
        color: '#333333',
        fontWeight: 'bold',
        paddingLeft: 2,
        fontSize: 14,
        marginBottom: '0.3rem'
    },
}

const priorityLabels = {
    NORMAL: 'Normal',
    URGENT: 'Urgente',
    EMERGENCY: 'Emergencial',
};

const statusLabels = {
    OPEN: 'Aberta',
    IN_PROGRESS: 'Em Andamento',
    COMPLETED: 'Concluída',
};

const zoneLabels = {
    URBAN: 'Urbana',
    RURAL: 'Rural',
};

const extensionUnitLabels = {
    M: 'm',
    KM: 'km',
};

const finalStatusLabels = {
    COMPLETED: '100% Concluído',
    PARTIALLY_COMPLETED: 'Parcialmente Concluído',
    STOPPED_RESCHEDULED: 'Paralisado / Reagendado',
};

const serviceLabels = {
    POTHOLE_REPAIR: 'Tapa-buracos',
    URBAN_PAVING: 'Asfaltamento de vias urbanas',
    RURAL_PAVING: 'Asfaltamento de vias rurais',
    RURAL_ROAD_MAINTENANCE: 'Manutenção de estradas rurais não pavimentadas',
    URBAN_ELECTRICAL_MAINTENANCE: 'Manutenção de rede elétrica urbana',
    RURAL_ELECTRICAL_MAINTENANCE: 'Manutenção de rede elétrica rural',
    STREET_CLEANING: 'Limpeza de vias públicas',
    PUBLIC_SPACE_CLEANING: 'Limpeza de espaços públicos',
    STORMWATER_DRAINAGE: 'Drenagem de águas pluviais',
    CURB_INSTALLATION: 'Execução de meios-fio',
    STORM_DRAIN_CLEANING: 'Limpeza de boca de lobo',
    STORM_DRAIN_CONSTRUCTION: 'Construção de boca de lobo',
    PUBLIC_SPACE_MAINTENANCE: 'Manutenção de espaços públicos',
    OTHER: 'Outros serviços correlatos',
};

const formatDate = (value) => (value ? moment(value).format('DD/MM/YYYY') : '-');

/* Exibe um campo da ocorrência interna somente para leitura, no padrão da aba de detalhes */
const Field = ({ label, value, full = false }) => (
    <strong style={{ display: 'flex', flexDirection: 'column', marginBottom: 16, color: '#41414d', gridColumn: full ? '1 / -1' : 'auto' }}>
        {label}
        <span style={{ fontWeight: 'normal', marginTop: 4, color: 'rgba(0, 0, 0, 0.54)' }}>
            {value === undefined || value === null || value === '' ? 'Não informado' : value}
        </span>
    </strong>
);

const listStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    columnGap: 24,
};

export default function ServiceOrderDetails() {

    const { id } = useParams();
    const userPermission = UserPermission();
    const navigate = useNavigate();

    const {state} = useLocation();

    /* Edição de workflow (prioridade/status/situação) liberada pela tela de gerenciamento */
    const canManage = state?.canManage === true;

    /* Acesso vindo de "Minhas Ocorrências": o criador edita o formulário completo */
    const isOwner = state?.isOwner === true;

    const from = state?.from || [
        { href: '/my-service-orders', name: 'Ocorrências Internas' },
        { href: '/my-service-orders', name: 'Minhas Ocorrências Internas' },
    ];

    const [notify, setNotify] = useState({ isOpen: false, message: '', type: '', title: '' });
    const [confirmDialog, setConfirmDialog] = useState({ isOpen: false, title: '', subTitle: '' });
    const [serviceOrder, setServiceOrder] = useState(null);

    /* Campos editáveis pelo administrador */
    const [prioritySelect, setPrioritySelect] = useState('');
    const [statusSelect, setStatusSelect] = useState('');
    const [finalStatusSelect, setFinalStatusSelect] = useState('');

    /* Indica o carregamento da geração do documento */
    const [downloading, setDownloading] = useState(false);

    useEffect(() => {
        api.get(`/service-orders/${id}`)
            .then(response => {
                setServiceOrder(response.data);
                setPrioritySelect(response.data.priority ?? '');
                setStatusSelect(response.data.status ?? '');
                setFinalStatusSelect(response.data.execution?.finalStatus ?? '');
            })
            .catch(err => {
                setNotify({
                    isOpen: true,
                    message: 'Erro ao carregar a ocorrência interna, tente novamente.',
                    type: 'error',
                    title: 'Erro'
                });
            });
    }, [id]);

    /* Baixa a Ordem de Serviço (.docx) preenchida gerada pelo backend */
    async function handleDownloadDocument() {
        setDownloading(true);
        try {
            const response = await api.get(`/service-orders/${id}/document`, {
                responseType: 'blob',
            });

            const safeNumber = String(serviceOrder?.orderNumber ?? id).replace(/[^\w.-]+/g, '-');
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `ordem-servico-${safeNumber}.docx`);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            setNotify({
                isOpen: true,
                message: 'Erro ao gerar o documento da ordem de serviço.',
                type: 'error',
                title: 'Erro',
            });
        } finally {
            setDownloading(false);
        }
    }

    /* Chama o alerta para atualizar */
    function confirms(e) {
        e.preventDefault();

        setConfirmDialog({
            isOpen: true,
            title: `Deseja atualizar os dados da ocorrência interna ?`,
            subTitle: ``,
            onConfirm: () => { handleUpdate() }
        })
    }

    async function handleUpdate() {
        /* Envia apenas os campos realmente editáveis nesta tela.
           O execution não é reenviado por inteiro (as datas vêm como string do
           GET e disparam erro de validação); só a situação final é atualizada.
           O backend só aceita string em finalStatus, então o campo é omitido
           quando vazio em vez de enviar null. */
        const data = {
            priority: prioritySelect,
            status: statusSelect,
        };

        if (finalStatusSelect !== '') {
            data.execution = { finalStatus: finalStatusSelect };
        }

        try {
            await api.patch(`/service-orders/${id}`, data);

            setServiceOrder({
                ...serviceOrder,
                ...data,
                execution: { ...(serviceOrder.execution ?? {}), ...data.execution },
            });

            setNotify({
                isOpen: true,
                message: 'Ocorrência interna atualizada com sucesso.',
                type: 'success',
                title: 'Atualizado'
            });
        } catch (err) {
            setNotify({
                isOpen: true,
                message: 'O sistema apresentou um erro na atualização',
                type: 'error',
                title: 'Erro'
            });
        }

        setConfirmDialog({
            ...confirmDialog,
            isOpen: false,
        });
    }

    /* Chama o alerta para deletar */
    function deleteServiceOrderDialog() {
        setConfirmDialog({
            isOpen: true,
            title: `Você gostaria de deletar a ocorrência interna "${serviceOrder.orderNumber}"?`,
            subTitle: 'Esta ação não poderá ser desfeita',
            onConfirm: () => { handleDelete() }
        })
    }

    async function handleDelete() {
        try {
            await api.delete(`/service-orders/${id}`);

            setConfirmDialog({
                ...confirmDialog,
                isOpen: false,
            });

            navigate('/manage-service-orders');
        } catch (err) {
            setNotify({
                isOpen: true,
                message: 'Erro ao deletar a ocorrência interna, tente novamente.',
                type: 'error',
                title: 'Erro'
            });
        }
    }

    const materialColumns = useMemo(() => [
        { Header: 'Material / Insumo', accessor: 'name' },
        { Header: 'Unidade', accessor: 'unit' },
        { Header: 'Qtd. Prevista', accessor: 'plannedQuantity' },
        { Header: 'Qtd. Utilizada', accessor: 'usedQuantity' },
    ], []);

    const assetColumns = useMemo(() => [
        { Header: 'Equipamento / Veículo', accessor: 'description' },
        { Header: 'Placa / Prefixo', accessor: 'plateOrPrefix' },
        { Header: 'Operador Responsável', accessor: 'operatorName' },
        { Header: 'Horas / Km Estimados', accessor: 'estimatedUsage' },
        { Header: 'Horas / Km Utilizados', accessor: 'actualUsage' },
    ], []);

    if (!serviceOrder) {
        return (
            <>
                <div style={{ marginTop: 32, marginLeft: 20 }}>
                    <Breadcrumb paths={[...from, { href: '', name: 'Detalhes' }]} />
                </div>

                <Card style={classes.root}>
                    <CardContent>
                        <div style={classes.message}>
                            Carregando as informações da ocorrência interna...
                        </div>
                    </CardContent>
                </Card>

                <Notification
                    notify={notify}
                    setNotify={setNotify}
                />
            </>
        );
    }

    const location = serviceOrder.location ?? {};
    const engineer = serviceOrder.engineer ?? {};
    const supervisor = serviceOrder.supervisor ?? {};
    const execution = serviceOrder.execution ?? {};

    return (
        <div className="details-container">
            <div style={{ marginTop: 32, marginLeft: 20 }}>
                <Breadcrumb paths={[...from, { href: '', name: 'Detalhes' }]} />
            </div>

            <Card style={classes.root}>
                <CardContent id='service-order-details' className="information-complaints">

                    <Typography variant="h5" align="center" style={classes.textTitle}>
                        Informações da Ocorrência Interna
                    </Typography>

                    <div style={classes.message}>
                        Consulte abaixo todas as informações cadastradas para a ocorrência interna
                        nº {serviceOrder.orderNumber}.
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Identificação
                        </Typography>

                        <ul style={listStyle}>
                            <Field label="Nº da Ordem de Serviço:" value={serviceOrder.orderNumber} />
                            <Field label="Data de Emissão:" value={formatDate(serviceOrder.issuedAt)} />
                            <Field label="Prazo de Execução (dias):" value={serviceOrder.executionDeadlineDays} />
                            <Field label="Data Máxima de Conclusão:" value={formatDate(serviceOrder.maxCompletionDate)} />
                            <Field label="Prioridade:" value={priorityLabels[serviceOrder.priority]} />
                            <Field label="Status:" value={statusLabels[serviceOrder.status]} />
                            <Field label="Solicitante / Origem:" value={serviceOrder.requesterOrigin} />
                            <Field label="Criada por:" value={serviceOrder.userName} />
                            <Field label="Criada em:" value={formatDate(serviceOrder.createdAt)} />
                        </ul>
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Localização
                        </Typography>

                        <ul style={listStyle}>
                            <Field label="Logradouro / Via Pública:" value={location.street} />
                            <Field label="Zona:" value={zoneLabels[location.zone]} />
                            <Field label="Bairro / Localidade / Vila:" value={location.neighborhood} />
                            <Field label="Trecho / Extensão:" value={location.extension} />
                            <Field label="Unidade:" value={extensionUnitLabels[location.extensionUnit]} />
                            <Field label="Ponto de Referência / GPS:" value={location.referencePoint} />
                            <Field label="Latitude:" value={location.latitude} />
                            <Field label="Longitude:" value={location.longitude} />
                        </ul>
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Especificação dos Serviços a Serem Executados
                        </Typography>

                        <ul style={listStyle}>
                            <Field
                                label="Serviços:"
                                value={(serviceOrder.services ?? [])
                                    .map(service => serviceLabels[service] ?? service)
                                    .join(', ')}
                                full
                            />
                            <Field
                                label="Descrição Detalhada do Trabalho / Metragem / Especificações Complementares:"
                                value={serviceOrder.serviceDescription}
                                full
                            />
                        </ul>
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Responsáveis
                        </Typography>

                        <ul style={listStyle}>
                            <Field label="Engenheiro / Fiscal Responsável:" value={engineer.name} />
                            <Field label="CREA:" value={engineer.crea} />
                            <Field label="Matrícula do Engenheiro:" value={engineer.registration} />
                            <Field label="Encarregado / Líder de Equipe:" value={supervisor.name} />
                            <Field label="Matrícula do Encarregado:" value={supervisor.registration} />
                            <Field label="Tel. de Contato:" value={supervisor.phone} />
                        </ul>
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Materiais / Insumos
                        </Typography>

                        <Table
                            columns={materialColumns}
                            data={serviceOrder.materials ?? []}
                            hideSearch
                            hidePagination
                        />
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Equipamentos / Veículos Alocados
                        </Typography>

                        <Table
                            columns={assetColumns}
                            data={serviceOrder.allocatedAssets ?? []}
                            hideSearch
                            hidePagination
                        />
                    </div>

                    <div style={classes.section}>
                        <Typography style={classes.subTitle}>
                            Execução
                        </Typography>

                        <ul style={listStyle}>
                            <Field label="Data Início Efetivo:" value={formatDate(execution.startedAt)} />
                            <Field label="Data Conclusão Efetiva:" value={formatDate(execution.completedAt)} />
                            <Field label="Situação Final do Serviço:" value={finalStatusLabels[execution.finalStatus]} />
                            <Field
                                label="Observações / Justificativas Técnicas do Encarregado de Campo:"
                                value={execution.technicalNotes}
                                full
                            />
                        </ul>
                    </div>

                    <Grid container justifyContent="flex-end" style={{ marginTop: 16 }}>
                        <Button
                            variant="contained"
                            onClick={handleDownloadDocument}
                            disabled={downloading}
                            startIcon={downloading ? <CircularProgress size={18} style={{ color: '#fff' }} /> : null}
                            style={{ backgroundColor: '#1551B3', color: '#fff', borderRadius: 20, textTransform: 'none', fontWeight: 'bold' }}
                        >
                            {downloading ? 'Gerando...' : 'Imprimir'}
                        </Button>
                    </Grid>
                </CardContent>
            </Card>

            {isOwner ? (
                <ServiceOrderRegister
                    serviceOrder={serviceOrder}
                    onSaved={(data) => setServiceOrder({ ...serviceOrder, ...data })}
                />
            ) : canManage && userPermission === 'admin' ? (
                <form onSubmit={confirms}>
                    <Card style={classes.root}>
                        <CardContent id='service-order-update'>

                            <Grid container alignItems="center" justifyContent="space-between">
                                <Grid item xs={11}>
                                    <Typography variant="h5" align="center" style={classes.textTitle}>
                                        Editar Ocorrência Interna
                                    </Typography>
                                </Grid>

                                <Grid item xs={1} style={{ textAlign: 'right' }}>
                                    {userPermission === 'admin' ? (
                                        <Link to="#">
                                            <FaTrashAlt
                                                size={20}
                                                color="#1351B4"
                                                onClick={() => {
                                                    deleteServiceOrderDialog()
                                                }}
                                            />
                                        </Link>
                                    ) : (
                                        false
                                    )}
                                </Grid>
                            </Grid>

                            <div style={classes.message}>
                                Atualize abaixo a prioridade, o status e a situação final desta
                                ocorrência interna.
                            </div>

                            <Grid container spacing={2} style={{ marginTop: 10 }}>
                                <Grid item xs={12} md={4}>
                                    <Typography style={classes.labelInput}>
                                        Prioridade:
                                    </Typography>

                                    <FormControl variant="outlined" fullWidth>
                                        <Select
                                            value={prioritySelect}
                                            onChange={e => setPrioritySelect(e.target.value)}
                                            style={{ 'background': '#fff' }}
                                        >
                                            {Object.entries(priorityLabels).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography style={classes.labelInput}>
                                        Status:
                                    </Typography>

                                    <FormControl variant="outlined" fullWidth>
                                        <Select
                                            value={statusSelect}
                                            onChange={e => setStatusSelect(e.target.value)}
                                            style={{ 'background': '#fff' }}
                                        >
                                            {Object.entries(statusLabels).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>

                                <Grid item xs={12} md={4}>
                                    <Typography style={classes.labelInput}>
                                        Situação Final do Serviço:
                                    </Typography>

                                    <FormControl variant="outlined" fullWidth>
                                        <Select
                                            value={finalStatusSelect}
                                            onChange={e => setFinalStatusSelect(e.target.value)}
                                            style={{ 'background': '#fff' }}
                                        >
                                            {Object.entries(finalStatusLabels).map(([value, label]) => (
                                                <MenuItem key={value} value={value}>{label}</MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>

                    <Grid container spacing={2} alignItems="center" justifyContent="center">
                        <Grid item>
                            <Button
                                variant="outlined"
                                type='submit'
                                startIcon={<UpdateIcon style={{ 'color': '#fff' }} />}
                                style={classes.buttonUpdate}
                            >
                                Atualizar
                            </Button>
                        </Grid>
                    </Grid>

                    <div style={{ height: 40 }} />
                </form>
            ) : (
                false
            )}

            <Notification
                notify={notify}
                setNotify={setNotify}
            />

            <ConfirmDialog
                confirmDialog={confirmDialog}
                setConfirmDialog={setConfirmDialog}
            />
        </div>
    );
}
